import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { fmtINR, fmtNum } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loading, SectionTitle } from '@/components/Shared';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Droplets, FlaskConical, Minus, Plus, ShoppingCart, Trash2, MapPin, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const SLOTS = [
  { label: '09:00 AM - 12:00 PM', h: 9 },
  { label: '12:00 PM - 03:00 PM', h: 12 },
  { label: '03:00 PM - 09:00 PM', h: 15 },
];

const CAT_META = {
  Lubricants: { label: 'Lube', icon: Droplets },
  DEF: { label: 'DEF (AdBlue)', icon: FlaskConical },
};

export default function ProductShop() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const cat = ['Lubricants', 'DEF'].includes(params.get('cat')) ? params.get('cat') : 'Lubricants';
  const [products, setProducts] = useState(null);
  const [cart, setCart] = useState({}); // product_id -> qty
  const [view, setView] = useState('browse'); // 'browse' | 'checkout' | 'review'
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState('');
  const [schedDate, setSchedDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [schedSlot, setSchedSlot] = useState('9');
  const [slotAvail, setSlotAvail] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [payment, setPayment] = useState('Credit');
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [idemKey] = useState(() => Math.random().toString(36).slice(2));

  useEffect(() => {
    api.get('/products').then((r) => setProducts(r.data.filter((p) => ['Lubricants', 'DEF'].includes(p.category))));
    api.get('/customers/me/addresses').then((r) => {
      setAddresses(r.data);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def) setAddressId(def.id);
    });
  }, []);

  useEffect(() => {
    if (!schedDate) { setSlotAvail(null); return; }
    api.get(`/orders/slot-availability?date=${schedDate}`)
      .then((r) => setSlotAvail(r.data))
      .catch(() => setSlotAvail(null));
  }, [schedDate]);

  const slotInfo = (h) => {
    const s = slotAvail?.slots?.[String(h)];
    if (s) return { remaining: s.remaining, full: s.full, allowed: s.allowed, reason: s.reason };
    return { remaining: 3, full: false, allowed: true, reason: null };
  };

  const addr = addresses.find((a) => a.id === addressId);
  const byId = useMemo(() => Object.fromEntries((products || []).map((p) => [p.id, p])), [products]);
  const cartItems = Object.entries(cart).filter(([, q]) => q > 0);
  const cartCount = cartItems.length;
  const cartTotal = cartItems.reduce((s, [pid, q]) => {
    const p = byId[pid];
    return p ? s + p.price * q * (1 + (p.gst || 0) / 100) : s;
  }, 0);

  const setQty = (p, q) => {
    const stock = Number(p.available_qty || 0);
    if (q > stock) { toast.error(`Only ${fmtNum(stock)} ${p.unit} of ${p.name} in stock`); q = stock; }
    setCart((c) => {
      const n = { ...c };
      if (q <= 0) delete n[p.id]; else n[p.id] = q;
      return n;
    });
  };
  const addToCart = (p) => {
    if (Number(p.available_qty || 0) < p.min_order_qty) return toast.error(`${p.name} is out of stock`);
    setQty(p, p.min_order_qty || 1);
  };

  const getQuote = async () => {
    if (!addr) return toast.error('Add a delivery address first (Profile)');
    setBusy(true);
    try {
      const r = await api.post('/orders/products/quote', {
        items: cartItems.map(([pid, q]) => ({ product_id: pid, qty: Number(q) })),
        lat: addr.lat, lng: addr.lng,
      });
      setQuote(r.data); setView('review');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to get quote'); } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!schedDate) return toast.error('Pick a delivery date');
    setBusy(true);
    try {
      const slot = SLOTS.find((s) => String(s.h) === schedSlot) || SLOTS[0];
      const dt = new Date(`${schedDate}T${String(slot.h).padStart(2, '0')}:00:00`);
      const pretty = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const r = await api.post('/orders/products', {
        items: cartItems.map(([pid, q]) => ({ product_id: pid, qty: Number(q) })),
        address_id: addressId, lat: addr.lat, lng: addr.lng, address_text: addr.address,
        delivery_time: `${pretty} · ${slot.label}`, schedule_type: 'scheduled',
        scheduled_at: dt.toISOString(), slot_date: schedDate, slot_hour: slot.h,
        instructions, payment_method: payment, idempotency_key: idemKey,
      });
      toast.success('Order placed!');
      nav(`/app/track/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to place order'); } finally { setBusy(false); }
  };

  if (!products) return <Loading />;
  const list = products.filter((p) => p.category === cat);

  // ---------------- Browse ----------------
  if (view === 'browse') {
    return (
      <div className="space-y-4 pb-24">
        <SectionTitle>Lube & DEF Shop</SectionTitle>
        <Tabs value={cat} onValueChange={(v) => setParams({ cat: v })}>
          <TabsList className="w-full">
            {Object.entries(CAT_META).map(([c, m]) => (
              <TabsTrigger key={c} value={c} className="flex-1" data-testid={`shop-tab-${c.toLowerCase()}`}>
                <m.icon className="h-4 w-4 mr-1.5" />{m.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {list.length === 0 && <p className="text-sm text-muted-foreground col-span-2">No products available in this category yet.</p>}
          {list.map((p) => {
            const inCart = cart[p.id] || 0;
            const out = Number(p.available_qty || 0) < (p.min_order_qty || 1);
            return (
              <div key={p.id} className="rounded-xl border border-border bg-card p-4 hover:shadow-md transition-shadow" data-testid={`shop-product-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{[p.brand, p.pack_size].filter(Boolean).join(' · ')}</p>
                  </div>
                  {out ? <Badge variant="destructive" className="shrink-0">Out of stock</Badge>
                    : <Badge variant="secondary" className="shrink-0">{fmtNum(p.available_qty)} {p.unit} left</Badge>}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div>
                    <span className="font-display text-lg font-semibold">{fmtINR(p.price)}</span>
                    <span className="text-xs text-muted-foreground">/{p.unit}</span>
                    <span className="text-[11px] text-muted-foreground ml-2">+ GST {p.gst}%</span>
                  </div>
                  {inCart > 0 ? (
                    <div className="flex items-center gap-1.5">
                      <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => setQty(p, inCart - (inCart <= p.min_order_qty ? inCart : 1))} data-testid={`cart-minus-${p.id}`}><Minus className="h-4 w-4" /></Button>
                      <Input type="number" className="h-8 w-16 text-center" value={inCart}
                        onChange={(e) => setQty(p, Math.max(0, Number(e.target.value)))} data-testid={`cart-qty-${p.id}`} />
                      <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => setQty(p, inCart + 1)} data-testid={`cart-plus-${p.id}`}><Plus className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => addToCart(p)} disabled={out} data-testid={`add-to-cart-${p.id}`}>
                      <Plus className="h-4 w-4 mr-1" />Add
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">Min order: {fmtNum(p.min_order_qty)} {p.unit}</p>
              </div>
            );
          })}
        </div>

        {cartCount > 0 && (
          <div className="fixed bottom-16 inset-x-0 z-30 px-4 pb-2">
            <div className="max-w-2xl mx-auto">
              <Button className="w-full h-12 shadow-lg" onClick={() => setView('checkout')} data-testid="cart-checkout-button">
                <ShoppingCart className="h-5 w-5 mr-2" />
                {cartCount} item{cartCount > 1 ? 's' : ''} · {fmtINR(cartTotal)} — Checkout
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------------- Checkout (address + schedule + payment) ----------------
  if (view === 'checkout') {
    return (
      <div className="space-y-4 max-w-lg mx-auto">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setView('browse')} data-testid="checkout-back-button"><ArrowLeft className="h-5 w-5" /></Button>
          <h2 className="font-display text-xl font-semibold">Checkout</h2>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-2" data-testid="checkout-cart-summary">
          <p className="text-sm font-semibold">Your Cart</p>
          {cartItems.map(([pid, q]) => {
            const p = byId[pid];
            if (!p) return null;
            return (
              <div key={pid} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{p.name} × {fmtNum(q)} {p.unit}</span>
                <span className="flex items-center gap-2">{fmtINR(p.price * q)}
                  <button onClick={() => setQty(p, 0)} aria-label={`Remove ${p.name}`} data-testid={`cart-remove-${pid}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                </span>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div>
            <Label>Delivery Location</Label>
            {addresses.length === 0 ? (
              <div className="text-sm text-muted-foreground mt-2">No address. <Button variant="link" className="px-1" onClick={() => nav('/app/profile')}>Add one</Button></div>
            ) : (
              <Select value={addressId} onValueChange={setAddressId}>
                <SelectTrigger data-testid="shop-address-select" className="mt-1"><SelectValue placeholder="Select address" /></SelectTrigger>
                <SelectContent>{addresses.map((a) => <SelectItem key={a.id} value={a.id}>{a.label} — {a.address}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label>Delivery Schedule</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><Label className="text-xs">Date</Label><Input type="date" min={new Date().toISOString().slice(0, 10)} value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="shop-schedule-date" /></div>
              <div><Label className="text-xs">Time Slot</Label>
                <Select value={schedSlot} onValueChange={setSchedSlot}>
                  <SelectTrigger data-testid="shop-schedule-slot"><SelectValue placeholder="Slot" /></SelectTrigger>
                  <SelectContent>{SLOTS.map((s) => { const info = slotInfo(s.h); return (
                    <SelectItem key={s.h} value={String(s.h)} disabled={!info.allowed}>
                      {s.label} {info.full ? '— Full' : (info.allowed ? `— ${info.remaining} left` : '— Unavailable')}
                    </SelectItem>
                  ); })}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div><Label>Delivery Instructions</Label><Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Call security on arrival" data-testid="shop-instructions-input" /></div>
          <div>
            <Label>Payment Method</Label>
            <RadioGroup value={payment} onValueChange={setPayment} className="grid grid-cols-2 gap-2 mt-2">
              {['Credit', 'UPI', 'Net Banking', 'Cash'].map((m) => (
                <label key={m} className={`flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer ${payment === m ? 'border-primary bg-muted' : 'border-border'}`}>
                  <RadioGroupItem value={m} data-testid={`shop-pay-${m.replace(/\s+/g, '-').toLowerCase()}`} /><span className="text-sm">{m}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <Button className="w-full h-11" onClick={getQuote} disabled={busy || !addressId || cartCount === 0} data-testid="shop-review-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Review Order'}
          </Button>
        </div>
      </div>
    );
  }

  // ---------------- Review + confirm ----------------
  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => setView('checkout')}><ArrowLeft className="h-5 w-5" /></Button>
        <h2 className="font-display text-xl font-semibold">Review Order</h2>
      </div>
      {quote && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="shop-review-card">
          <div className="flex items-start gap-2 text-sm"><MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" /><div><p>{addr?.label}</p><p className="text-muted-foreground">{addr?.address}</p></div></div>
          <div className="border-t border-border pt-3 space-y-1.5 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tax Invoice (combined)</p>
            {quote.items.map((it) => (
              <Row key={it.product_id} l={`${it.name} · ${fmtNum(it.qty)} ${it.unit} × ${fmtINR(it.unit_price)} (GST ${it.gst_pct}%)`} r={fmtINR(it.line_subtotal)} />
            ))}
            <Row l="Items Subtotal" r={fmtINR(quote.subtotal)} />
            <Row l={`Delivery Charge`} r={quote.delivery_charge === 0 ? 'FREE' : fmtINR(quote.delivery_charge)} />
            <div className="border-t border-dashed border-border pt-2 mt-1" />
            <Row l="CGST" r={fmtINR(quote.cgst_amount)} />
            <Row l="SGST" r={fmtINR(quote.sgst_amount)} />
            <div className="border-t border-border pt-2 flex justify-between font-semibold text-base">
              <span>Grand Total</span><span data-testid="shop-total-value">{fmtINR(quote.total)}</span>
            </div>
            <p className="text-[11px] text-muted-foreground">One combined TAX INVOICE will be generated for products + delivery (CGST + SGST).</p>
          </div>
          <Button className="w-full h-12" onClick={confirm} disabled={busy} data-testid="shop-confirm-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" />Confirm Order</>}
          </Button>
        </div>
      )}
    </div>
  );
}

const Row = ({ l, r }) => <div className="flex justify-between gap-3"><span className="text-muted-foreground">{l}</span><span className="shrink-0">{r}</span></div>;
